import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Download, Check, ArrowRight, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const LeadCapture = ({ visitorId, result, onComplete }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        visitor_id: visitorId,
        email: email
      });
      setIsSubmitted(true);
      toast.success("Results sent to your email!");
    } catch (error) {
      console.error("Failed to save lead:", error);
      toast.error("Failed to send results");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewScan = () => {
    onComplete();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen flex flex-col items-center justify-center relative px-6"
      data-testid="lead-capture"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-green/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-lg mx-auto text-center">
        {!isSubmitted ? (
          <>
            {/* Email icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="w-20 h-20 mx-auto mb-8 rounded-full bg-gradient-to-br from-neon-purple/20 to-neon-cyan/20 flex items-center justify-center border border-white/10"
            >
              <Mail className="w-10 h-10 text-neon-purple" />
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-4 tracking-wider"
            >
              Get Your Results
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-rajdhani text-white/60 mb-8"
            >
              Enter your email to receive your superhero identity card and detailed analysis
            </motion.p>

            {/* Email form */}
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onSubmit={handleSubmit}
              className="glass p-8 rounded-lg"
            >
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent border-b-2 border-white/20 focus:border-neon-purple rounded-none text-lg py-3 px-0 text-white placeholder:text-white/30 font-rajdhani mb-6"
                data-testid="email-input"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-neon-purple text-white font-orbitron tracking-widest uppercase rounded-sm hover:bg-neon-purple/80 transition-all shadow-[0_0_20px_rgba(127,90,240,0.4)] flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="submit-email-btn"
              >
                {isSubmitting ? (
                  <>Processing...</>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Send My Results
                  </>
                )}
              </button>
            </motion.form>

            {/* Skip option */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              onClick={handleNewScan}
              className="mt-6 text-white/40 hover:text-white/60 transition-colors font-rajdhani underline underline-offset-4"
              data-testid="skip-email-btn"
            >
              Start a new scan
            </motion.button>
          </>
        ) : (
          <>
            {/* Success state */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-neon-green/20 flex items-center justify-center border border-neon-green/50"
            >
              <Check className="w-12 h-12 text-neon-green" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-orbitron text-2xl sm:text-3xl font-bold text-white mb-4 tracking-wider"
            >
              You're All Set!
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-rajdhani text-white/60 mb-8"
            >
              Check your inbox for your superhero identity card
            </motion.p>

            {/* Summary card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass p-6 rounded-lg mb-8 text-left"
            >
              <h3 className="font-orbitron text-lg text-neon-purple mb-2">
                {result?.superhero_title}
              </h3>
              <p className="font-rajdhani text-white/80 italic">
                "{result?.one_liner}"
              </p>
            </motion.div>

            {/* New scan button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              onClick={handleNewScan}
              className="px-8 py-4 bg-neon-purple text-white font-orbitron tracking-widest uppercase rounded-sm hover:bg-neon-purple/80 transition-all shadow-[0_0_20px_rgba(127,90,240,0.4)] flex items-center gap-3 mx-auto"
              data-testid="new-scan-btn"
            >
              <RefreshCw className="w-5 h-5" />
              Scan Another Hero
            </motion.button>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default LeadCapture;
